import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Bus, Phone, Mail } from "lucide-react";
import { siteConfig } from "@/config/siteConfig";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Button } from "@/components/ui/button";
import logo from "@/assets/logo.png";

export function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Home", href: isHome ? "#home" : "/" },
    { name: "Services", href: isHome ? "#services" : "/#services" },
    { name: "About", href: isHome ? "#about" : "/#about" },
    { name: "Features", href: isHome ? "#features" : "/#features" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <>
      {/* Top Bar */}
      <div className="hidden md:block bg-secondary/80 backdrop-blur-sm border-b border-border/50">
        <div className="container mx-auto px-4 py-2">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-6">
              <a 
                href={`tel:${siteConfig.locations[0].phone.replace(/\s/g, '')}`}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <Phone className="h-4 w-4" />
                {siteConfig.locations[0].phone}
              </a>
              <a 
                href={`tel:${siteConfig.locations[1].phone.replace(/\s/g, '')}`}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
              >
                <Phone className="h-4 w-4" />
                {siteConfig.locations[1].phone}
              </a>
            </div>
            <ThemeToggle />
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav
        className={`fixed top-0 md:top-[44px] left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? "glass-strong shadow-lg py-3"
            : "bg-transparent py-4"
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link to="/" className="flex items-center group">
              <img 
                src={logo} 
                alt="CityLink Logo" 
                className="h-16 w-auto transition-all duration-300 group-hover:scale-105"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="px-4 py-2 text-foreground/80 hover:text-primary font-medium transition-colors animated-underline"
                >
                  {link.name}
                </a>
              ))}
            </div>

            {/* CTA + Mobile Menu */}
            <div className="flex items-center gap-3">
              <div className="md:hidden">
                <ThemeToggle />
              </div>
              {siteConfig.hasLuggageTracking && (
                <Button
                  asChild
                  variant="outline"
                  className="hidden sm:inline-flex border-primary/50 hover:bg-primary/10 font-semibold px-6 hover-lift"
                >
                  <a href="https://www.cbsc.co.za/citylinkluggagetrack" target="_blank" rel="noopener noreferrer">Track Luggage</a>
                </Button>
              )}
              {siteConfig.bookingEnabled ? (
                <Button
                  asChild
                  className="hidden sm:inline-flex bg-primary hover:bg-primary/90 text-primary-foreground font-semibold px-6 hover-lift"
                >
                  <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer">Book Now</a>
                </Button>
              ) : (
                <Button
                  disabled
                  className="hidden sm:inline-flex bg-muted text-muted-foreground font-semibold px-6 cursor-not-allowed opacity-60 pointer-events-none"
                >
                  Booking Soon
                </Button>
              )}
              <button
                className="lg:hidden p-2 text-foreground"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          <div
            className={`lg:hidden overflow-hidden transition-all duration-300 ${
              isMobileMenuOpen ? "max-h-96 mt-4" : "max-h-0"
            }`}
          >
            <div className="glass rounded-2xl p-4 space-y-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  className="block px-4 py-3 text-foreground hover:text-primary hover:bg-primary/10 rounded-lg transition-all"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {link.name}
                </a>
              ))}
              {siteConfig.hasLuggageTracking && (
                <Button
                  asChild
                  variant="outline"
                  className="w-full mt-4 border-primary/50 hover:bg-primary/10"
                >
                  <a href="https://www.cbsc.co.za/citylinkluggagetrack" target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)}>Track Luggage</a>
                </Button>
              )}
              {siteConfig.bookingEnabled ? (
                <Button
                  asChild
                  className="w-full mt-2 bg-primary hover:bg-primary/90 text-primary-foreground"
                >
                  <a href={siteConfig.bookingUrl} target="_blank" rel="noopener noreferrer" onClick={() => setIsMobileMenuOpen(false)}>Book Now</a>
                </Button>
              ) : (
                <Button
                  disabled
                  className="w-full mt-2 bg-muted text-muted-foreground cursor-not-allowed opacity-60 pointer-events-none"
                >
                  Booking Soon
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>
    </>
  );
}
